const STORAGE_KEY = "viewedProducts";
const SECTION_SELECTOR = "[data-recently-viewed-section]";
const SWIPER_SELECTOR = "swiper-container[data-recently-viewed-swiper]";
const ARROWS_SELECTOR = "[data-rv-swiper-arrows]";
const ROOT_MARGIN = "500px";

const getStoredHandles = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Recently viewed storage unavailable", error);
    return null;
  }
};

const parseHandles = (value) => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map((handle) => String(handle).trim()).filter(Boolean);
    }
  } catch (error) {
    // fall through to string parsing
  }

  return value
    .replace(/[\[\]]/g, "")
    .split(",")
    .map((handle) => handle.replace(/"/g, "").trim())
    .filter(Boolean);
};

const dedupe = (items) => {
  const seen = new Set();
  const result = [];

  items.forEach((item) => {
    if (seen.has(item)) {
      return;
    }
    seen.add(item);
    result.push(item);
  });

  return result;
};

const waitForSwiperInstance = (swiperEl) => {
  if (!swiperEl) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    if (swiperEl.swiper) {
      resolve(swiperEl.swiper);
      return;
    }

    const check = () => {
      if (swiperEl.swiper) {
        resolve(swiperEl.swiper);
        return;
      }
      requestAnimationFrame(check);
    };

    check();
  });
};

const fetchProductCard = async (handle) => {
  const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
  const url = `${root}products/${encodeURIComponent(handle)}?section_id=product-card`;
  const response = await fetch(url, { credentials: "same-origin" });

  if (!response.ok) {
    throw new Error(`Section render failed with status ${response.status}`);
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  const productCard = doc.querySelector("product-card");

  if (!productCard) {
    return null;
  }

  const productId = productCard.getAttribute("data-product-id");

  if (productId === null || productId === "") {
    return null;
  }

  return productCard.outerHTML;
};

const rebindSwiperNav = (swiperEl) => {
  if (!swiperEl || !swiperEl.swiper) return;
  const swiper = swiperEl.swiper;
  swiper.update();
  if (swiper.navigation) {
    swiper.navigation.destroy();
    swiper.navigation.init();
    swiper.navigation.update();
  }
};

const initTabs = (section) => {
  const tabs = section.querySelectorAll("[data-rv-tab]");
  if (tabs.length < 2) return;

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const panelId = tab.dataset.rvTab;

      // Find currently active panel before switching
      const prevPanel = section.querySelector("[data-rv-panel]:not([hidden])");

      tabs.forEach((t) => t.classList.toggle("active", t === tab));
      section.querySelectorAll("[data-rv-panel]").forEach((p) => {
        p.hidden = p.dataset.rvPanel !== panelId;
      });

      // Destroy nav on the swiper we just hid (releases shared arrow ownership)
      if (prevPanel && prevPanel.dataset.rvPanel !== panelId) {
        const prevSwiperEl = prevPanel.querySelector("swiper-container");
        if (prevSwiperEl && prevSwiperEl.swiper && prevSwiperEl.swiper.navigation) {
          prevSwiperEl.swiper.navigation.destroy();
        }
      }

      // Re-init nav on the newly visible swiper so it claims the shared arrows
      const activePanel = section.querySelector(`[data-rv-panel="${panelId}"]`);
      if (activePanel) {
        rebindSwiperNav(activePanel.querySelector("swiper-container"));
      }
    });
  });
};

class RecentlyViewedSlider {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector(SWIPER_SELECTOR);
    this.arrows = section.querySelector(ARROWS_SELECTOR);
    this.maxProducts = Number(section.dataset.maxProducts || 12);
    this.hasFeaturedCollection = section.dataset.hasFeaturedCollection === "true";

    if (!this.slider) {
      return;
    }

    const stored = getStoredHandles();
    const handles = dedupe(parseHandles(stored)).slice(-this.maxProducts).reverse();

    if (!handles.length) {
      this.handleEmpty();
      return;
    }

    this.handles = handles;
    this.initObserver();
  }

  initObserver() {
    this.slider.setAttribute("hidden", "hidden");
    this.slider.setAttribute("aria-busy", "true");

    this.observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (!isVisible) {
          return;
        }
        this.observer.disconnect();
        this.load();
      },
      {
        rootMargin: `${ROOT_MARGIN} 0px ${ROOT_MARGIN}`,
      },
    );

    this.observer.observe(this.section);
  }

  async load() {
    if (!window.customElements || !("whenDefined" in window.customElements)) {
      this.handleEmpty();
      return;
    }

    await window.customElements.whenDefined("swiper-container");

    let appended = 0;
    const fragment = document.createDocumentFragment();

    for (const handle of this.handles) {
      try {
        const markup = await fetchProductCard(handle);
        if (!markup) {
          continue;
        }

        const slide = document.createElement("swiper-slide");
        slide.classList.add("h-auto");
        slide.innerHTML = markup;
        fragment.appendChild(slide);
        appended += 1;
      } catch (error) {
        console.warn(`Recently viewed product failed for handle "${handle}"`, error);
      }
    }

    // Single DOM manipulation after all slides are prepared
    if (appended > 0) {
      this.slider.appendChild(fragment);
    }

    if (appended === 0) {
      this.handleEmpty();
      return;
    }

    this.slider.classList.remove("swiper-loading");
    this.slider.removeAttribute("hidden");
    this.slider.removeAttribute("aria-busy");

    // Only apply loop/deferred init when loop is enabled in settings
    const loopRequested = this.slider.dataset.loopRequested === "true";
    if (loopRequested) {
      const minSlidesForLoop = Number(this.slider.dataset.minSlidesForLoop || 2);

      if (appended >= minSlidesForLoop) {
        this.slider.setAttribute("loop", "true");
      } else {
        this.slider.removeAttribute("loop");
        console.warn(
          `Recently Viewed: loop disabled due to insufficient slides. Need at least ${minSlidesForLoop}, got ${appended}.`,
        );
      }

      // Initialize Swiper now that slides are in the DOM (init="false" in markup)
      if (
        this.slider.getAttribute("init") === "false" &&
        typeof this.slider.initialize === "function"
      ) {
        this.slider.initialize();
      }
    }

    // Only claim the shared arrows if the RV panel is currently visible.
    // If collection tab is active, the arrows already belong to that swiper.
    const rvPanel = this.section.querySelector('[data-rv-panel="recently-viewed"]');
    const rvIsActive = rvPanel && !rvPanel.hidden;

    if (rvIsActive) {
      if (this.arrows) {
        this.arrows.hidden = appended <= 1;
      }
      const swiper = await waitForSwiperInstance(this.slider);
      if (swiper) rebindSwiperNav(this.slider);
    }
  }

  handleEmpty() {
    if (this.slider) {
      this.slider.classList.remove("swiper-loading");
      this.slider.setAttribute("hidden", "hidden");
      this.slider.removeAttribute("aria-busy");
    }

    // If a featured collection tab exists, switch to it instead of hiding the section.
    // Don't hide the shared arrows — the collection swiper still needs them.
    if (this.hasFeaturedCollection) {
      const rvTab = this.section.querySelector('[data-rv-tab="recently-viewed"]');
      const collectionTab = this.section.querySelector('[data-rv-tab="collection"]');
      if (rvTab) rvTab.hidden = true;
      if (collectionTab) collectionTab.click();
      return;
    }

    if (this.arrows) {
      this.arrows.hidden = true;
    }

    this.section.hidden = true;
  }
}

const bindActivePanelNav = (section) => {
  const activePanel = section.querySelector("[data-rv-panel]:not([hidden])");
  if (!activePanel) return;
  const swiperEl = activePanel.querySelector("swiper-container");
  if (swiperEl) {
    waitForSwiperInstance(swiperEl).then(() => rebindSwiperNav(swiperEl));
  }
};

const initRecentlyViewed = () => {
  const sections = document.querySelectorAll(SECTION_SELECTOR);
  sections.forEach((section) => {
    initTabs(section);

    // Bind arrows to the initially active panel's swiper (handles server-rendered swipers)
    bindActivePanelNav(section);

    // Re-bind after recommendations AJAX injects the collection swiper.
    // The RV swiper initialises first and locks the shared arrows (swiper-button-lock +
    // swiper-button-disabled on both buttons → CSS hides the div). We must destroy the
    // RV swiper's navigation before binding the collection swiper so those classes are
    // removed. The injected swiper also has navigation="false" so we wire params manually.
    section.addEventListener("recommendations:loaded", () => {
      const arrows = section.querySelector(ARROWS_SELECTOR);

      // Step 1: release the RV swiper's lock on the shared arrows
      const rvPanel = section.querySelector('[data-rv-panel="recently-viewed"]');
      if (rvPanel) {
        const rvSwiperEl = rvPanel.querySelector("swiper-container");
        if (rvSwiperEl && rvSwiperEl.swiper && rvSwiperEl.swiper.navigation) {
          rvSwiperEl.swiper.navigation.destroy();
        }
      }

      // Step 2: wire and bind the collection swiper
      const activePanel = section.querySelector("[data-rv-panel]:not([hidden])");
      if (!activePanel) return;

      const swiperEl = activePanel.querySelector("swiper-container");
      if (!swiperEl) return;

      waitForSwiperInstance(swiperEl).then((swiper) => {
        if (!swiper) return;

        if (arrows) {
          // Wire navigation params — the injected swiper has navigation="false" so
          // params.navigation won't have nextEl/prevEl set.
          // Swiper expects CSS selector strings (it calls document.querySelector internally),
          // NOT DOM element references — passing elements causes querySelector to return null.
          const nav = swiper.params.navigation;
          if (arrows.id && (!nav || nav === false || !nav.nextEl)) {
            // Must include Swiper's navigation defaults — navigation.update() calls
            // disabledClass.split() etc. and crashes if these are missing.
            swiper.params.navigation = {
              enabled: true,
              nextEl: `#${arrows.id} .swiper-button-next`,
              prevEl: `#${arrows.id} .swiper-button-prev`,
              hideOnClick: false,
              disabledClass: "swiper-button-disabled",
              hiddenClass: "swiper-button-hidden",
              lockClass: "swiper-button-lock",
              navigationDisabledClass: "swiper-navigation-disabled",
            };
          }
          // Un-hide in case handleEmpty hid the arrows before AJAX completed
          arrows.hidden = false;
        }

        rebindSwiperNav(swiperEl);
      });
    });

    new RecentlyViewedSlider(section);
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRecentlyViewed);
} else {
  initRecentlyViewed();
}
